import React from "react";
import { Link } from "react-router-dom";
import { Overline } from "@/components/brand/SectionDivider";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import { AmbientLight } from "@/components/brand/AmbientLight";
import Licensing from "@/components/home/Licensing";
import AGMethod from "@/components/home/AGMethod";
import useDocumentTitle from "@/lib/useDocumentTitle";

export default function TheFirm() {
  useDocumentTitle("The Firm | AG Public Adjusting");
  return (
    <main data-testid="firm-page" className="pt-32 bg-midnight-gradient">
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <AmbientLight size={1200} color="blue" intensity={0.5} style={{ top: "-10%", left: "30%" }} />
        <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" aria-hidden>
          <ConcentricMonogram size={500} variant="ghost" glowColor="blue" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <Overline className="mb-8">The Firm</Overline>
          <h1 className="font-display-tight text-white text-[clamp(2.8rem,6vw,6rem)] leading-[0.95] tracking-[-0.045em] max-w-5xl">
            An institution
            <br />
            <span className="gold-metallic font-display font-light">for Florida policyholders.</span>
          </h1>
          <p className="mt-12 text-ag-text-secondary text-lg md:text-xl leading-[1.65] font-light max-w-3xl">
            AG Public Adjusting exists for one reason: to represent policyholders during property insurance
            claims. The firm operates with the structure of a professional services organization —
            documented methodology, multi-disciplinary review, and a written record on every file.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <Overline className="mb-8">Our Mandate</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,3.5vw,3rem)] leading-[1.05] tracking-[-0.035em]">
              One side. <span className="gold-metallic font-display font-light">Always.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <p className="text-ag-text-secondary text-lg leading-[1.7] font-light">
              The firm represents the policyholder. The firm does not represent insurance carriers,
              brokers, or any party whose interests stand opposite the insured. This is not a marketing
              position — it is the regulatory and ethical foundation of public adjusting practice in Florida.
            </p>
            <p className="text-ag-text-secondary text-lg leading-[1.7] font-light">
              AG Public Adjusting was built around this principle. Every internal decision, every engagement
              term, and every file the firm touches is governed by it.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/5 bg-midnight-deeper">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <Overline className="mb-8">Our Practice</Overline>
            <h2 className="font-display-tight text-white text-[clamp(2rem,3.5vw,3rem)] leading-[1.05] tracking-[-0.035em]">
              Built as a firm.
              <br />
              <span className="gold-metallic font-display font-light">Operated as a system.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <p className="text-ag-text-secondary text-lg leading-[1.7] font-light">
              Files are assigned, reviewed, and managed within a documented methodology — the AG Method.
              Senior reviewers, field adjusters, documentation specialists, and negotiators operate as
              defined roles, not interchangeable individuals.
            </p>
            <p className="text-ag-text-secondary text-lg leading-[1.7] font-light">
              This produces consistency. It produces a written record. And it produces recoveries that
              do not depend on any single person — only on the system.
            </p>
          </div>
        </div>
      </section>

      <AGMethod />
      <Licensing />

      <section className="py-24 lg:py-32 border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Overline className="mb-8">Engage the Firm</Overline>
          <h2 className="font-display-tight text-white text-[clamp(2.5rem,5vw,4rem)] mb-10 leading-[0.98] tracking-[-0.04em]">
            Begin a <span className="gold-metallic font-display font-light">free evaluation.</span>
          </h2>
          <Link to="/free-claim-evaluation" data-testid="firm-cta-evaluation" className="btn-gold inline-flex items-center gap-3">
            Free Claim Evaluation →
          </Link>
        </div>
      </section>
    </main>
  );
}
